import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from "@/lib/i18n";
import {
  Download,
  Maximize,
  Minimize,
  Keyboard,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import KeyboardShortcutsModal from '@/components/svg-editor/header-panel/keyboard-shortcuts-modal';

export default function Header({
  selectedCount,
  onExportSVG,
  onLoadJSON,
  onClearProject,
  projectName: externalProjectName,
  onProjectNameChange,
  theme = 'dark',
}) {
  const { t } = useTranslation('editor');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);
  const [projectName, setProjectName] = useState(externalProjectName || t('new_project'));


  useEffect(() => {
    if (externalProjectName !== undefined) {
      setProjectName(externalProjectName);
    }
  }, [externalProjectName]);

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setProjectName(newName);
    if (onProjectNameChange) {
      onProjectNameChange(newName);
    }
  };

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Помилка увімкнення повноекранного режиму: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    const handleKeyDown = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) {
        return;
      }

      if (e.key === 'F11') {
        e.preventDefault();
        toggleFullscreen();
      }

      if (e.altKey && e.key === 'Enter') {
        e.preventDefault();
        toggleFullscreen();
      }

      if (e.key === '?') {
        e.preventDefault();
        setIsShortcutsOpen((prev) => !prev);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleFullscreen]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onLoadJSON(file);
      e.target.value = '';
    }
  };

  return (
    <header className={`flex items-center justify-between p-2.5 rounded-lg border select-none transition-colors duration-300 ${
      theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-slate-200 shadow-sm'
    }`}>
      <div className="flex items-center gap-3">
        <h2 className={`text-sm font-semibold ${theme === 'dark' ? 'text-zinc-200' : 'text-slate-800'}`}>{t('header.editor_title')}</h2>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${
          theme === 'dark' ? 'bg-zinc-800 text-zinc-400 border-zinc-700' : 'bg-slate-100 text-slate-500 border-slate-200'
        }`}>
          EZCAD Ready
        </span>

        <div className="relative flex items-center">
          <Input
            type="text"
            value={projectName}
            onChange={handleNameChange}
            placeholder={t('header.project_name_placeholder')}
            className={`h-8 w-[32rem] text-xs transition-colors ${
              theme === 'dark' 
                ? 'bg-zinc-950/60 border-zinc-700/60 text-zinc-200 focus:border-indigo-500' 
                : 'bg-slate-50 border-slate-200 text-slate-900 focus:border-indigo-500'
            } focus-visible:ring-1 focus-visible:ring-indigo-500`}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className={`text-xs mr-2 ${theme === 'dark' ? 'text-zinc-400' : 'text-slate-500'}`}>
          {t('header.selected')}: <span className={`${theme === 'dark' ? 'text-white' : 'text-slate-900'} font-medium`}>{selectedCount}</span>
        </span>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className={`text-red-400 hover:text-red-300 hover:bg-red-950/50 border-red-900/50 ${
                theme === 'light' ? 'bg-white hover:bg-red-50' : ''
              }`}
              title={t('header.clear_project')}
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              {t('header.clear')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className={`${theme === 'dark' ? 'bg-zinc-900 border-zinc-800 text-zinc-100' : 'bg-white border-slate-200 text-slate-900'}`}>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('header.clear_confirm_title')}</AlertDialogTitle>
              <AlertDialogDescription className={theme === 'dark' ? 'text-zinc-400' : 'text-slate-500'}>
                {t('header.clear_confirm_desc')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className={`${
                theme === 'dark' 
                  ? 'bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700 hover:text-white' 
                  : 'bg-slate-100 border-slate-200 text-slate-800 hover:bg-slate-200'
              }`}>
                {t('header.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={onClearProject}
                className="bg-red-600 hover:bg-red-500 text-white font-medium"
              >
                {t('header.clear')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className={`w-px h-5 mx-1 ${theme === 'dark' ? 'bg-zinc-800' : 'bg-slate-200'}`} />

        <Button
          size="sm"
          variant="ghost"
          onClick={() => setIsShortcutsOpen(true)}
          title={`${t('header.shortcuts')} (?)`}
          className={theme === 'dark' ? 'text-zinc-400 hover:text-zinc-200' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}
        >
          <Keyboard className="w-4 h-4 mr-1.5" />
          {t('header.shortcuts')}
        </Button>

        <Button
          size="sm"
          variant="outline"
          onClick={toggleFullscreen}
          className={theme === 'light' ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50' : ''}
          title={isFullscreen ? `${t('header.exit_fullscreen')} (Esc / F11)` : `${t('header.fullscreen')} (F11 або Alt+Enter)`}
        >
          {isFullscreen ? (
            <Minimize className="w-4 h-4 mr-1.5" />
          ) : (
            <Maximize className="w-4 h-4 mr-1.5" />
          )}
          {isFullscreen ? t('header.exit_fullscreen') : t('header.fullscreen')}
        </Button>
        <Button
          size="sm"
          variant="default"
          onClick={() => onExportSVG?.(projectName)}
          className="bg-emerald-600 hover:bg-emerald-500 text-white font-medium"
        >
          <Download className="w-4 h-4 mr-1.5" />
          {t('header.export_svg')}
        </Button>
      </div>

      <KeyboardShortcutsModal
        open={isShortcutsOpen}
        onOpenChange={setIsShortcutsOpen}
      />
    </header>
  );
}